import { useState } from 'react';

import { PlaceholderMemberService } from '@splitfiesta/core';
import { useNamespace, useTranslation } from '@splitfiesta/i18n';
import type { PlaceholderClaimSummaryDto } from '@splitfiesta/shared';
import { Alert, Button } from '@splitfiesta/ui';

import {
  useConfirmPlaceholderClaimMutation,
  useRequestPlaceholderClaimMutation,
} from '@/hooks/queries/useGroups';

import { getErrorMessage, handleError } from '@/utils/errorHandler';

import { ClaimConfirmDialog } from './ClaimConfirmDialog';

export interface PlaceholderClaimBlockProps {
  groupId: string;
  placeholderId: string;
  placeholderName: string;
  groupName: string;
  /** Absent on an unclaimed row, which is what the Claim pill is offered on. */
  claim?: PlaceholderClaimSummaryDto | null;
  viewerId: string;
  userRole?: 'owner' | 'admin' | 'member';
  isOnline: boolean;
  /** Whether the viewer may not claim another placeholder in this group. */
  viewerCannotClaim?: boolean;
  /** Fired once the claim request succeeds. */
  onClaimRequested?: () => void;
  className?: string;
}

const PILL = 'rounded-full px-3 py-1 text-xs';

/**
 * The placeholder row's second line: the Claim pill (behind one
 * confirmation), the merge's progress, or a failed merge with the retry for a
 * decider. Which of those it is comes from core's `claimBlockState` — this
 * component renders the outcome and never re-derives it.
 */
export function PlaceholderClaimBlock({
  groupId,
  placeholderId,
  placeholderName,
  groupName,
  claim,
  viewerId,
  userRole,
  isOnline,
  viewerCannotClaim,
  onClaimRequested,
  className = '',
}: PlaceholderClaimBlockProps) {
  const { t } = useTranslation();
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);
  // The retry response arrives before the refetched member row does; a merge
  // that failed again has to stay retryable in the meantime.
  const [echo, setEcho] = useState<{
    /** The claim the echo was recorded against; a refetch past it drops it. */
    seen: string;
    claim: PlaceholderClaimSummaryDto;
  } | null>(null);
  const retryClaim = useConfirmPlaceholderClaimMutation(groupId);
  const requestClaim = useRequestPlaceholderClaimMutation(groupId);

  useNamespace('groups');

  const seen = claim
    ? `${claim.claimantId}:${claim.status}:${claim.canRetry}`
    : '';
  const shown = claim && echo?.seen === seen ? echo.claim : claim;

  const state = PlaceholderMemberService.claimBlockState({
    claim: shown,
    viewerId,
    userRole,
    viewerCannotClaim,
  });

  if (state === 'none') {
    return null;
  }

  const actions = PlaceholderMemberService.claimActions(state);
  const busy = retryClaim.isPending || requestClaim.isPending;
  const actionsDisabled = !isOnline || busy;
  const offlineTitle = !isOnline
    ? t('groups:placeholderMembers.offlineDisabled')
    : undefined;
  const line = PlaceholderMemberService.claimLineKey(state);

  const run = async <T,>(
    call: () => Promise<T>,
    onResolved?: (result: T) => void
  ) => {
    if (!isOnline || busy) {
      return;
    }

    setError('');

    try {
      const result = await call();
      onResolved?.(result);
    } catch (err: unknown) {
      setError(getErrorMessage(handleError(err, 'groups'), t));
    }
  };

  const handleConfirmClaim = async () => {
    await run(
      () => requestClaim.mutateAsync({ userId: placeholderId }),
      () => onClaimRequested?.()
    );
    setConfirming(false);
  };

  const handleRetry = () => {
    if (!claim) return;
    void run(
      () =>
        retryClaim.mutateAsync({
          userId: placeholderId,
          claimantId: claim.claimantId,
        }),
      retried => {
        if (retried?.status === 'failed' || retried?.status === 'merging') {
          setEcho({
            seen,
            claim: {
              status: retried.status,
              claimantId: claim.claimantId,
              claimantName: claim.claimantName,
              canRetry: retried.canRetry,
            },
          });
        }
      }
    );
  };

  return (
    <div
      className={`space-y-1.5 ${className}`}
      data-testid={`claim-block-${placeholderId}`}
    >
      <div className="flex flex-wrap items-center gap-2">
        {line && (
          <p className="text-xs text-gray-600 dark:text-gray-300">
            {t(`groups:placeholderMembers.${line}`)}
          </p>
        )}
        {actions.includes('claim') && (
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setConfirming(true)}
            disabled={actionsDisabled}
            title={offlineTitle}
            className={`${PILL} border border-gray-300 dark:border-gray-600`}
            data-testid="claim-request"
          >
            {t('groups:placeholderMembers.claim')}
          </Button>
        )}
        {actions.includes('retry') && (
          <Button
            variant="primary"
            size="sm"
            onClick={handleRetry}
            disabled={actionsDisabled}
            title={offlineTitle}
            className={PILL}
            data-testid="claim-retry"
          >
            {t('common:errors.retry')}
          </Button>
        )}
      </div>

      {error && (
        <Alert type="error" message={error} data-testid="claim-error" />
      )}

      <ClaimConfirmDialog
        open={confirming}
        name={placeholderName}
        groupName={groupName}
        busy={requestClaim.isPending}
        onConfirm={() => void handleConfirmClaim()}
        onCancel={() => setConfirming(false)}
      />
    </div>
  );
}

export default PlaceholderClaimBlock;
